import React, { useState } from 'react';
import { Pencil, Trash2, X, Save, AlertTriangle } from 'lucide-react';

const linhaVazia = {
  id: null,
  nome: '',
  vendedor_nome: '',
  colaborador_nome: '',
  tipo: 'comercial',
  ativo: true,
};

/**
 * Modal de CRUD de presets de comissão.
 * Lista os presets, permite criar/editar inline e excluir.
 *
 * Props:
 *  - aberto, onFechar
 *  - presets, loading, error, criar, atualizar, remover (do hook useComissaoPresets)
 *  - vendedoresN8n, colaboradoresN8n — listas brutas pra ajudar a preencher os inputs
 */
const PresetsAdmin = ({
  aberto,
  onFechar,
  presets = [],
  loading = false,
  error = null,
  criar,
  atualizar,
  remover,
  vendedoresN8n = [],
  colaboradoresN8n = [],
}) => {
  const [form, setForm] = useState(linhaVazia);
  const [editandoId, setEditandoId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);

  if (!aberto) return null;

  const resetForm = () => {
    setForm(linhaVazia);
    setEditandoId(null);
  };

  const iniciarEdicao = (preset) => {
    setForm({
      id: preset.id,
      nome: preset.nome || '',
      vendedor_nome: preset.vendedor_nome || '',
      colaborador_nome: preset.colaborador_nome || '',
      tipo: preset.tipo || 'comercial',
      ativo: preset.ativo !== false,
    });
    setEditandoId(preset.id);
    setFeedback(null);
  };

  const handleSalvar = async () => {
    if (!form.nome.trim() || !form.vendedor_nome.trim() || !form.colaborador_nome.trim()) {
      setFeedback({ tipo: 'erro', msg: 'Preencha nome, vendedor e colaborador' });
      return;
    }
    setSalvando(true);
    setFeedback(null);
    const dados = {
      nome: form.nome.trim(),
      vendedor_nome: form.vendedor_nome.trim(),
      colaborador_nome: form.colaborador_nome.trim(),
      tipo: form.tipo || 'comercial',
      ativo: !!form.ativo,
    };
    const res = editandoId
      ? await atualizar(editandoId, dados)
      : await criar(dados);
    setSalvando(false);
    if (res.success) {
      resetForm();
      setFeedback({ tipo: 'ok', msg: editandoId ? 'Preset atualizado' : 'Preset cadastrado' });
    } else {
      setFeedback({ tipo: 'erro', msg: res.error || 'Falha ao salvar' });
    }
  };

  const handleRemover = async (id) => {
    if (!window.confirm('Remover este preset?')) return;
    const res = await remover(id);
    if (!res.success) {
      setFeedback({ tipo: 'erro', msg: res.error || 'Falha ao remover' });
    } else {
      setFeedback({ tipo: 'ok', msg: 'Preset removido' });
      if (editandoId === id) resetForm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--border)]">
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Presets de Comissão
          </h2>
          <button
            onClick={onFechar}
            className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {feedback && (
            <div
              className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${
                feedback.tipo === 'ok'
                  ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]'
                  : 'bg-[color:var(--surface-muted)] text-[color:var(--status-warn)]'
              }`}
            >
              {feedback.tipo === 'erro' && <AlertTriangle className="h-4 w-4" />}
              <span>{feedback.msg}</span>
            </div>
          )}

          {/* Formulário inline */}
          <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-md p-4 space-y-3">
            <h3 className="text-sm font-medium text-[color:var(--text-primary)]">
              {editandoId ? 'Editando preset' : 'Novo preset'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[color:var(--text-muted)] mb-1">Nome (label)</label>
                <input
                  type="text"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md px-3 py-2 text-sm text-[color:var(--text-primary)]"
                  placeholder="Ex.: KATRINE VANESSA (Loja)"
                  value={form.nome}
                  onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs text-[color:var(--text-muted)] mb-1">Vendedor (n8n)</label>
                <input
                  list="presetadmin-vendedores"
                  type="text"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md px-3 py-2 text-sm text-[color:var(--text-primary)]"
                  placeholder="Nome exato vindo do select de vendedor"
                  value={form.vendedor_nome}
                  onChange={(e) => setForm(f => ({ ...f, vendedor_nome: e.target.value }))}
                />
                <datalist id="presetadmin-vendedores">
                  {vendedoresN8n.map(v => <option key={v} value={v} />)}
                </datalist>
              </div>

              <div>
                <label className="block text-xs text-[color:var(--text-muted)] mb-1">Colaborador (n8n)</label>
                <input
                  list="presetadmin-colaboradores"
                  type="text"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md px-3 py-2 text-sm text-[color:var(--text-primary)]"
                  placeholder="Nome exato vindo do select de colaborador"
                  value={form.colaborador_nome}
                  onChange={(e) => setForm(f => ({ ...f, colaborador_nome: e.target.value }))}
                />
                <datalist id="presetadmin-colaboradores">
                  {colaboradoresN8n.map(v => <option key={v} value={v} />)}
                </datalist>
              </div>

              <div className="flex items-end gap-4">
                <div>
                  <label className="block text-xs text-[color:var(--text-muted)] mb-1">Tipo de comissão</label>
                  <select
                    className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md px-3 py-2 text-sm text-[color:var(--text-primary)]"
                    value={form.tipo}
                    onChange={(e) => setForm(f => ({ ...f, tipo: e.target.value }))}
                  >
                    <option value="comercial">Comercial — 15%</option>
                    <option value="loja">Loja — 10%</option>
                    <option value="fixo">Fixo — R$ 15/venda</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-[color:var(--text-primary)] pb-2">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) => setForm(f => ({ ...f, ativo: e.target.checked }))}
                  />
                  Ativo
                </label>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleSalvar}
                disabled={salvando}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-[color:var(--accent)] text-white hover:opacity-90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {salvando ? 'Salvando…' : (editandoId ? 'Atualizar' : 'Cadastrar')}
              </button>
              {editandoId && (
                <button
                  onClick={resetForm}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* Lista */}
          <div>
            <h3 className="text-sm font-medium text-[color:var(--text-primary)] mb-2">
              Presets cadastrados {presets.length > 0 && <span className="text-[color:var(--text-muted)]">({presets.length})</span>}
            </h3>
            {loading && <div className="text-sm text-[color:var(--text-muted)]">Carregando…</div>}
            {error && <div className="text-sm text-[color:var(--status-warn)]">Erro: {error}</div>}
            {!loading && !error && presets.length === 0 && (
              <div className="text-sm text-[color:var(--text-muted)]">Nenhum preset cadastrado ainda.</div>
            )}
            {presets.length > 0 && (
              <div className="border border-[color:var(--border)] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[color:var(--surface-muted)]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-[color:var(--text-muted)] text-xs uppercase">Nome</th>
                      <th className="text-left px-3 py-2 font-medium text-[color:var(--text-muted)] text-xs uppercase">Vendedor</th>
                      <th className="text-left px-3 py-2 font-medium text-[color:var(--text-muted)] text-xs uppercase">Colaborador</th>
                      <th className="text-center px-3 py-2 font-medium text-[color:var(--text-muted)] text-xs uppercase">Tipo</th>
                      <th className="text-center px-3 py-2 font-medium text-[color:var(--text-muted)] text-xs uppercase">Ativo</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {presets.map(p => (
                      <tr key={p.id} className="border-t border-[color:var(--border)]">
                        <td className="px-3 py-2 text-[color:var(--text-primary)]">{p.nome}</td>
                        <td className="px-3 py-2 text-[color:var(--text-muted)]">{p.vendedor_nome}</td>
                        <td className="px-3 py-2 text-[color:var(--text-muted)]">{p.colaborador_nome}</td>
                        <td className="px-3 py-2 text-center">
                          {(p.tipo === 'loja') && <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--accent)]/10 text-[color:var(--accent)]">Loja 10%</span>}
                          {(p.tipo === 'fixo') && <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">Fixo R$15</span>}
                          {(!p.tipo || p.tipo === 'comercial') && <span className="text-[color:var(--text-muted)]">Comercial</span>}
                        </td>
                        <td className="px-3 py-2 text-center">{p.ativo ? '✓' : '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => iniciarEdicao(p)}
                              className="text-[color:var(--text-muted)] hover:text-[color:var(--accent)]"
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRemover(p.id)}
                              className="text-[color:var(--text-muted)] hover:text-[color:var(--status-warn)]"
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[color:var(--border)] flex justify-end">
          <button
            onClick={onFechar}
            className="px-4 py-2 rounded-md text-sm border border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PresetsAdmin;
